import React from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AfsolSectionCard } from "@/components/afsol/AfsolSectionCard";
import { COUNTRY_OPTIONS } from "@/utils/afsol";
import { Search } from "lucide-react";

const TextInput = ({ label, value, onChange, testId, type = "text", readOnly = false }) => (
  <div className="space-y-1">
    <Label htmlFor={testId} className="text-[11px] font-medium text-slate-600">{label}</Label>
    <Input
      id={testId}
      type={type}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      readOnly={readOnly}
      className="h-9 rounded-sm border-slate-300 text-sm"
      data-testid={testId}
    />
  </div>
);

export const AfsolPartyPanel = ({
  prefix,
  title,
  party,
  onChange,
  onLookup,
  lookupLoading,
  showRoleTabs = false,
  showNotificationToggle = false,
}) => {
  const updateField = (field, value) => onChange({ ...party, [field]: value });
  const updateSecondarySigner = (field, value) => onChange({
    ...party,
    secondarySigner: { ...party.secondarySigner, [field]: value },
  });

  return (
    <AfsolSectionCard title={title} testId={`${prefix}-panel`}>
      <div className="space-y-3">
        {showRoleTabs ? (
          <div className="flex gap-2">
            {[
              { key: "medeigandi", label: "Meðeigandi" },
              { key: "umradamadur", label: "Umráðamaður" },
            ].map((option) => (
              <Button
                key={option.key}
                type="button"
                variant={party.roleMode === option.key ? "default" : "outline"}
                size="sm"
                onClick={() => updateField("roleMode", option.key)}
                className="h-8 rounded-sm px-3 text-xs"
                data-testid={`${prefix}-role-${option.key}-button`}
              >
                {option.label}
              </Button>
            ))}
          </div>
        ) : null}

        <div className="grid gap-3 lg:grid-cols-[1fr_auto_auto]">
          <TextInput
            label="Kennitala"
            value={party.kennitala}
            onChange={(value) => updateField("kennitala", value.replace(/\D/g, "").slice(0, 10))}
            testId={`${prefix}-kennitala-input`}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => onLookup("party")}
            disabled={lookupLoading || !party.kennitala}
            className="mt-6 h-9 w-9 rounded-sm border-slate-300"
            data-testid={`${prefix}-kennitala-lookup-button`}
          >
            <Search className="h-4 w-4" />
          </Button>
          <label className="mt-6 flex items-center gap-2 text-xs text-slate-600" data-testid={`${prefix}-deceased-wrapper`}>
            <Checkbox
              checked={Boolean(party.isDeceased)}
              onCheckedChange={(checked) => updateField("isDeceased", Boolean(checked))}
              data-testid={`${prefix}-deceased-checkbox`}
            />
            Dánarbú
          </label>
        </div>

        <TextInput label="Nafn" value={party.name} onChange={(value) => updateField("name", value)} testId={`${prefix}-name-input`} />

        <div className="grid gap-3 md:grid-cols-2">
          <TextInput label="Heimilisfang" value={party.address} onChange={(value) => updateField("address", value)} testId={`${prefix}-address-input`} />
          <div className="grid gap-3 md:grid-cols-2">
            <TextInput label="Póstnúmer" value={party.postalCode} onChange={(value) => updateField("postalCode", value)} testId={`${prefix}-postal-code-input`} />
            <TextInput label="Staður" value={party.city} onChange={(value) => updateField("city", value)} testId={`${prefix}-city-input`} />
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <TextInput label="Sími" value={party.phone} onChange={(value) => updateField("phone", value)} testId={`${prefix}-phone-input`} />
          <TextInput label="Netfang" value={party.email} onChange={(value) => updateField("email", value)} testId={`${prefix}-email-input`} />
        </div>

        <div className="grid gap-3 md:grid-cols-[1fr_auto]">
          <div className="space-y-1">
            <Label htmlFor={`${prefix}-nationality-select`} className="text-[11px] font-medium text-slate-600">Ríkisfang</Label>
            <select
              id={`${prefix}-nationality-select`}
              value={party.nationality || "Ísland"}
              onChange={(event) => updateField("nationality", event.target.value)}
              className="h-9 w-full rounded-sm border border-slate-300 px-3 text-sm"
              data-testid={`${prefix}-nationality-select`}
            >
              {COUNTRY_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          <label className="mt-6 flex items-center gap-2 text-xs text-slate-600">
            <Checkbox
              checked={Boolean(party.isPoliticallyExposed)}
              onCheckedChange={(checked) => updateField("isPoliticallyExposed", Boolean(checked))}
              data-testid={`${prefix}-pep-checkbox`}
            />
            Í áhættuhópi vegna stjórnmálalegra tengsla
          </label>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <TextInput label="Banki" value={party.banki} onChange={(value) => updateField("banki", value)} testId={`${prefix}-banki-input`} />
          <TextInput label="Höfuðbók" value={party.hofudbok} onChange={(value) => updateField("hofudbok", value)} testId={`${prefix}-hofudbok-input`} />
          <TextInput label="Reikningur" value={party.reikningur} onChange={(value) => updateField("reikningur", value)} testId={`${prefix}-reikningur-input`} />
        </div>

        <div className="space-y-3 border-t border-slate-200 pt-3">
          <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
            <Checkbox
              checked={Boolean(party.hasSecondarySigner)}
              onCheckedChange={(checked) => updateField("hasSecondarySigner", Boolean(checked))}
              data-testid={`${prefix}-secondary-signer-checkbox`}
            />
            Annar undirritar fyrir hönd lögaðila
          </label>

          {party.hasSecondarySigner ? (
            <div className="space-y-3 rounded-sm border border-slate-200 bg-slate-50 p-3" data-testid={`${prefix}-secondary-signer-section`}>
              <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
                <TextInput
                  label="Kennitala undirritara"
                  value={party.secondarySigner?.kennitala}
                  onChange={(value) => updateSecondarySigner("kennitala", value.replace(/\D/g, "").slice(0, 10))}
                  testId={`${prefix}-secondary-signer-kennitala-input`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => onLookup("secondary")}
                  disabled={lookupLoading || !party.secondarySigner?.kennitala}
                  className="mt-6 h-9 w-9 rounded-sm border-slate-300"
                  data-testid={`${prefix}-secondary-signer-lookup-button`}
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              <TextInput label="Nafn undirritara" value={party.secondarySigner?.name} onChange={(value) => updateSecondarySigner("name", value)} testId={`${prefix}-secondary-signer-name-input`} />
              <div className="grid gap-3 md:grid-cols-2">
                <TextInput label="Sími undirritara" value={party.secondarySigner?.phone} onChange={(value) => updateSecondarySigner("phone", value)} testId={`${prefix}-secondary-signer-phone-input`} />
                <TextInput label="Netfang undirritara" value={party.secondarySigner?.email} onChange={(value) => updateSecondarySigner("email", value)} testId={`${prefix}-secondary-signer-email-input`} />
              </div>
            </div>
          ) : null}
        </div>

        {showNotificationToggle ? (
          <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
            <Checkbox
              checked={Boolean(party.createOwnerNotification)}
              onCheckedChange={(checked) => updateField("createOwnerNotification", Boolean(checked))}
              data-testid={`${prefix}-owner-notification-checkbox`}
            />
            Útbúa þarf millitilkynningu frá eiganda til umráðamanns
          </label>
        ) : null}
      </div>
    </AfsolSectionCard>
  );
};

export default AfsolPartyPanel;